import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode, RefCallback } from 'react'
import { useT } from 'flyt/lib/language/language'
import { Button } from 'flyt/ui/Button/Button'

interface PagedRowsProps<Row> {
  rows: readonly Row[]
  pageSize?: number
  /** A change of filter or sort starts at page one. Data refreshes only clamp. */
  resetKey: string
  label: string
  children: (rows: readonly Row[], controls: ReactNode, pageStartRef: RefCallback<HTMLElement>) => ReactNode
}

/** Page direct rows only. The caller renders child folders separately. */
export function PagedRows<Row>({ rows, pageSize, resetKey, label, children }: PagedRowsProps<Row>) {
  const t = useT('common')
  const firstRow = useRef<HTMLElement | null>(null)
  const moveToStart = useRef(false)
  const pageStartRef = useCallback((element: HTMLElement | null) => { firstRow.current = element }, [])
  const size = pageSize === undefined ? Math.max(1, rows.length) : Math.max(1, Math.floor(pageSize))
  const pages = Math.max(1, Math.ceil(rows.length / size))
  const [state, setState] = useState({ key: resetKey, page: 1 })
  const page = state.key === resetKey ? Math.min(state.page, pages) : 1
  if (state.key !== resetKey || state.page !== page) setState({ key: resetKey, page })

  useEffect(() => {
    if (!moveToStart.current) return
    moveToStart.current = false
    firstRow.current?.focus({ preventScroll: true })
    firstRow.current?.scrollIntoView?.({ block: 'start' })
  }, [page])
  const goTo = (nextPage: number) => {
    moveToStart.current = true
    setState({ key: resetKey, page: nextPage })
  }
  const controls = pages > 1 ? (
    <nav aria-label={t('paging.label', { label })} className="flex flex-wrap items-center justify-end gap-2 whitespace-normal py-1">
      <span aria-live="polite" className="text-sm text-text-muted">
        {t('paging.range', { from: (page - 1) * size + 1, to: Math.min(page * size, rows.length), count: rows.length })}
      </span>
      <Button size="sm" variant="secondary" disabled={page === 1}
        onClick={() => goTo(page - 1)}>{t('paging.previous')}</Button>
      <Button size="sm" variant="secondary" disabled={page === pages}
        onClick={() => goTo(page + 1)}>{t('paging.next')}</Button>
    </nav>
  ) : null
  return children(rows.slice((page - 1) * size, page * size), controls, pageStartRef)
}
